/**
 * WebsiteGrid
 *
 * 3-column grid for website templates with hover-to-play cards and responsive layout
 */
import React, { useState, useRef, useEffect } from 'react';
import { Heart, Play, ExternalLink, Globe } from 'lucide-react';

function WebsiteCard({ website, onWebsiteClick, onSaveWebsite, isSaved, onImageLoaded, isPremiumUser = true }) {
  const [isHovered, setIsHovered] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isImagePreloaded, setIsImagePreloaded] = useState(false);
  const videoRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Convert Firebase Storage URLs
  const convertFirebaseStorageUrl = (gsUrl) => {
    if (!gsUrl) return gsUrl;
    
    // If it's already an HTTP URL (signed URL from backend), return as-is
    if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
      return gsUrl;
    }
    
    // Only convert gs:// URLs (for backward compatibility)
    if (!gsUrl.startsWith('gs://')) {
      return gsUrl; // Return as-is if not a gs:// URL
    }
    
    // Extract bucket and path from gs://bucket/path
    const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
    if (!match) {
      return gsUrl; // Return as-is if format doesn't match
    }
    
    const [, bucket, path] = match;
    // Convert to Firebase Storage HTTP URL
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  };

  const convertedVideoUrl = convertFirebaseStorageUrl(website.videoUrl);

  // Preload video for immediate playback
  useEffect(() => {
    if (videoRef.current && convertedVideoUrl) {
      videoRef.current.load();
    }
  }, [convertedVideoUrl]);

  // Preload thumbnail image
  useEffect(() => {
    const thumbnailUrl = website.thumbnailUrl;
    if (thumbnailUrl) {
      const img = new Image();
      img.src = thumbnailUrl;
      img.onload = () => {
        setIsImagePreloaded(true);
      };
      img.onerror = () => {
        setThumbnailError(true);
        setIsImagePreloaded(true);
      };
    }
  }, [website.thumbnailUrl]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Slight delay helps avoid quick enter/leave causing AbortError
    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && convertedVideoUrl) {
        try {
          videoRef.current.currentTime = 0;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch((error) => {
              // Ignore AbortError (play interrupted by a pause), do not mark as error
              const message = String(error?.message || error || '');
              if (error?.name === 'AbortError' || message.includes('pause()')) return;
              console.log('Video play failed:', error);
              setVideoError(true);
            });
          }
        } catch (error) {
          const message = String(error?.message || error || '');
          if (error?.name === 'AbortError' || message.includes('pause()')) return;
          setVideoError(true);
        }
      }
    }, 80);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Pause video
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (_) {}
    }
  };

  const handleCardClick = () => {
    onWebsiteClick(website);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    if (onSaveWebsite) {
      onSaveWebsite(website.id, !isSaved);
    }
  };

  const handleImageLoad = () => {
    if (!imageLoaded) {
      setImageLoaded(true);
      if (onImageLoaded) {
        onImageLoaded();
      }
    }
  };

  const handleImageError = () => {
    console.error(`Failed to load thumbnail for website: ${website.id}`);
    setThumbnailError(true);
    setImageLoaded(true);
    if (onImageLoaded) {
      onImageLoaded();
    }
  };

  // Force image loading check after a timeout to prevent hanging cards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded) {
        console.log(`Timeout loading thumbnail for website: ${website.id}`);
        setImageLoaded(true);
        if (onImageLoaded) {
          onImageLoaded();
        }
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [website.id, imageLoaded, onImageLoaded]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const thumbnailSrc = website.thumbnailUrl;

  return (
    <div 
      className="group relative transition-all duration-200 ease-in-out cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video/Thumbnail container with 16:9 aspect ratio */}
      <div className="relative overflow-hidden rounded-lg" style={{ aspectRatio: '16 / 9' }}>
        {/* Skeleton loader shown while image is loading */}
        {!isImagePreloaded && (
          <div className="bg-gray-200 dark:bg-gray-700 animate-pulse w-full h-full absolute inset-0">
          </div>
        )}

        {/* Video */}
        {convertedVideoUrl && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover z-10 ${
              isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{ transition: 'opacity 0.2s ease-in-out' }}
            crossOrigin="anonymous"
            muted
            playsInline
            loop
            preload="auto"
            onError={() => setVideoError(true)}
          >
            <source src={convertedVideoUrl} type="video/mp4" />
          </video>
        )}

        {/* Thumbnail - show when video is not playing or on error */}
        {thumbnailSrc && !thumbnailError && (
          <img
            src={thumbnailSrc}
            alt={website.title || 'Website preview'}
            className={`absolute inset-0 w-full h-full object-cover ${
              isImagePreloaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transition: 'opacity 0.3s ease-in-out' }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        )}

        {/* Fallback - show when thumbnail fails or video has error */}
        {(thumbnailError || videoError) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800">
            <ExternalLink className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-2" />
          </div>
        )}

        {/* Top-left direct website link (globe) - only visible on hover */}
        {isHovered && imageLoaded && website.websiteUrl && (
          <a
            href={website.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute top-2 left-2 z-30 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 animate-fadeIn"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
            aria-label="Open website"
          >
            <Globe className="w-5 h-5 text-gray-700 dark:text-gray-200" />
          </a>
        )}

        {/* Save button - only visible on hover or when saved */}
        {(isHovered || isSaved) && imageLoaded && (
          <button
            onClick={handleSaveClick}
            className={`absolute top-2 right-2 z-30 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 animate-fadeIn backdrop-blur-sm ${
              isSaved 
                ? 'bg-purple-500/90 text-white' 
                : 'bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300'
            }`}
            style={{ animation: 'fadeIn 0.2s ease-out' }}
            aria-label={isSaved ? 'Unsave website' : 'Save website'}
          >
            <Heart 
              className={`w-5 h-5 ${isSaved ? 'fill-white' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Title row removed (now shown on hover to match Video Templates) */}
    </div>
  );
}

function WebsiteGrid({
  websites,
  onWebsiteClick,
  onSaveWebsite,
  savedWebsiteIds = new Set(),
  loading = false,
  skeletonItems = [],
  isPremiumUser = true
}) {
  const [loadedImages, setLoadedImages] = useState(0);
  const totalImages = websites.length;

  const handleImageLoaded = () => {
    setLoadedImages(prev => prev + 1);
  };

  return (
    <div className="w-full">
      {/* Grid with 2 columns on desktop, 2 on tablet, 1 on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {websites.map((website) => (
          <WebsiteCard
            key={website.id}
            website={website}
            onWebsiteClick={onWebsiteClick}
            onSaveWebsite={onSaveWebsite}
            isSaved={savedWebsiteIds.has(website.id)}
            onImageLoaded={handleImageLoaded}
            isPremiumUser={isPremiumUser}
          />
        ))}

        {/* Skeleton items */}
        {loading && skeletonItems.map((_, index) => (
          <div key={`skeleton-${index}`} className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg" style={{ aspectRatio: '16 / 9' }}></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WebsiteGrid;

